import { useEffect, useRef } from "react";
import { BrowserRouter, HashRouter } from "react-router-dom";

import { AppRouter } from "@/routes";

// Electron loads the bundle via file:// (loadFile) — HTML5 history needs a
// real origin, so we swap to hash routing for the desktop build. Web stays
// on BrowserRouter so URLs (and shareable links) keep working.
const Router =
  import.meta.env.VITE_TARGET === "electron" ? HashRouter : BrowserRouter;
import { useRealtimeSubscriptions } from "@/realtime/use-realtime-subscriptions";
import { XpToasts } from "@/apps/games/progression/xp-toasts";
import { ThemeProvider } from "@/shell/theme-provider";
import { notificationPermission, registerPushSubscription } from "@/utils/web-notifications";
import { clearPushResubscribeFlag } from "@/utils/push-resubscribe-flag";
import { syncActiveProfileFromStorage, useProfileSwitcherStore } from "@/stores/profile-switcher-store";
import {
  isAuthenticated,
  refreshSession,
  isFatalAuthError,
  getStoredTokens,
  clearTokens,
  getProfile,
  mapProfileToUser,
  useUserStore,
} from "@slumbr/core";

function useAuthCheck() {
  const setUser = useUserStore((s) => s.setUser);
  const setAccessToken = useUserStore((s) => s.setAccessToken);
  const setLoading = useUserStore((s) => s.setLoading);
  const logout = useUserStore((s) => s.logout);

  useEffect(() => {
    async function checkAuth() {
      // VITE_DEV_AUTH_BYPASS signs a mock user in for local UI work against a
      // stub API. The flag is documented in .env.local and already honoured by
      // the shell layout; without this branch nothing ever set the user, so the
      // guard bounced every route to /login and the flag did nothing.
      if (import.meta.env.VITE_DEV_AUTH_BYPASS === "true") {
        setAccessToken("dev-bypass");
        setUser({
          id: "dev-user",
          username: "dev",
          displayName: "Dev User",
          email: "dev@localhost",
        } as Parameters<typeof setUser>[0]);
        return;
      }

      // No stored tokens — not authenticated
      const tokens = getStoredTokens();
      if (!tokens) {
        setLoading(false);
        return;
      }

      // If access token expired, try refreshing first
      let activeToken = tokens.accessToken;
      if (!isAuthenticated()) {
        try {
          const newTokens = await refreshSession();
          activeToken = newTokens.accessToken;
        } catch (err) {
          if (isFatalAuthError(err)) {
            // Refresh token is permanently invalid — force logout. Clear the dead tokens AND the persona choice
            // (clearTokens): leaving the persona behind made the next account to sign in on this device skip
            // adopting its own profile and send the previous user's profile id.
            clearTokens();
            logout();
            return;
          }
          // Transient error (offline, network issue) — keep the user logged in
          // and use the stored (possibly expired) token; API calls will fail
          // gracefully until connectivity is restored and a background refresh succeeds.
          setLoading(false);
          return;
        }
      }

      setAccessToken(activeToken);
      try {
        syncActiveProfileFromStorage(); // start from the stored choice + empty list (drops a signed-out account's state)
        let profile = await getProfile();
        // Fresh device: no local persona choice yet → adopt the
        // server-remembered one BEFORE anything fetches data, otherwise a
        // persona user's first conversation-list fetch hits the account
        // partition and renders an empty messenger.
        if (profile.lastActiveProfileId) {
          const switcher = useProfileSwitcherStore.getState();
          if (!switcher.activeProfileId) {
            switcher.hydrateActiveProfileFromServer(profile.lastActiveProfileId);
            try {
              profile = await getProfile(); // re-fetch AS the persona
            } catch {
              // keep the account profile; header applies to later requests
            }
          }
        }
        setUser(mapProfileToUser(profile));
      } catch {
        // Profile fetch failed — still set token; profile will load later
        setLoading(false);
      }
    }

    checkAuth();
  }, [setUser, setAccessToken, setLoading, logout]);

  return null as unknown as (() => void) | null;
}

function useTokenRefresh() {
  const setAccessToken = useUserStore((s) => s.setAccessToken);
  const logout = useUserStore((s) => s.logout);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  async function tryRefresh() {
    const tokens = getStoredTokens();
    if (!tokens?.refreshToken) return;

    // Only refresh when the access token is within 5 minutes of expiry
    // (isAuthenticated returns false within 60s, but we want to refresh proactively)
    try {
      const payload = JSON.parse(atob(tokens.accessToken.split(".")[1])) as { exp: number };
      const expiresInMs = payload.exp * 1000 - Date.now();
      // Don't refresh if still more than 5 minutes away
      if (expiresInMs > 5 * 60 * 1000) return;
    } catch {
      // Can't parse token — attempt refresh anyway
    }

    try {
      const newTokens = await refreshSession();
      setAccessToken(newTokens.accessToken);
    } catch (err) {
      if (isFatalAuthError(err)) {
        // Refresh token permanently invalid — log out, clearing the dead tokens + persona choice (see boot path).
        clearTokens();
        logout();
      }
      // Otherwise silently swallow — transient failure, will retry on next interval
    }
  }

  useEffect(() => {
    // Check every 4 minutes — will only actually call Cognito when token is within 5 min of expiry
    intervalRef.current = setInterval(() => {
      tryRefresh().catch(() => {});
    }, 4 * 60 * 1000);

    // Also refresh when the tab becomes visible again (e.g. returning from sleep/another tab)
    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        tryRefresh().catch(() => {});
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setAccessToken, logout]);
}

function usePushSubscription() {
  const isAuthenticated = useUserStore((s) => s.isAuthenticated);
  useEffect(() => {
    if (!isAuthenticated) return;
    if (notificationPermission() !== "granted") return;

    const reRegister = () => {
      void registerPushSubscription()
        .then((ok) => {
          // Successful register satisfies any pending SW resubscribe flag
          // (set by the SW's pushsubscriptionchange handler — see sw.ts).
          if (ok) return clearPushResubscribeFlag().catch(() => {});
        })
        .catch(() => {});
    };

    // Re-register on every authenticated boot so subscription stays in sync
    // with the server even after token rotation / SW updates. This also
    // covers the IDB resubscribe flag: the SW already re-subscribed at the
    // browser level, so this call POSTs the fresh subscription with auth.
    reRegister();

    // The SW pings us when the browser rotates the push subscription while
    // the app is open — re-register immediately instead of waiting for the
    // next boot.
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const handleMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string } | undefined;
      if (data?.type !== "slumbr.push-resubscribe") return;
      reRegister();
    };
    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, [isAuthenticated]);
}

/**
 * Service worker → page navigation bridge.
 *
 * When the user clicks a desktop OS notification, the SW (sw.ts
 * notificationclick handler) focuses the existing window and posts a
 * `slumbr.navigate` message instead of using the unreliable
 * `WindowClient.navigate()` API. We catch the message here and push the
 * target into history; React Router's BrowserRouter listens for `popstate`
 * and renders the new route without a hard reload.
 */
function useServiceWorkerNavigationBridge() {
  useEffect(() => {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return;
    const handleMessage = (event: MessageEvent) => {
      const data = event.data as { type?: string; target?: string } | undefined;
      if (data?.type !== "slumbr.navigate" || !data.target) return;
      try {
        window.history.pushState({}, "", data.target);
        window.dispatchEvent(new PopStateEvent("popstate"));
      } catch (err) {
        // Last-ditch fallback. Hard reload is ugly but the user clicked a
        // notification — landing on the right page beats landing nowhere.
        console.warn("[app] SW navigate bridge failed, falling back:", err);
        window.location.href = data.target;
      }
    };
    navigator.serviceWorker.addEventListener("message", handleMessage);
    return () => {
      navigator.serviceWorker.removeEventListener("message", handleMessage);
    };
  }, []);
}

export function App() {
  useAuthCheck();
  useTokenRefresh();
  useRealtimeSubscriptions();
  usePushSubscription();
  useServiceWorkerNavigationBridge();

  return (
    <Router
      future={{ v7_startTransition: true, v7_relativeSplatPath: true }}
    >
      <ThemeProvider>
        <AppRouter />
        <XpToasts />
      </ThemeProvider>
    </Router>
  );
}
